import React, { useCallback, useEffect, useState } from "react";
import { Truck, AlertTriangle, Power, Copy } from "react-feather";
import ToggleSwitch from "../../shared/ToggleSwitch";
// Only successes are toasted here — fulfillmentApi already surfaces every failure,
// including the server's "N customers are still waiting" refusal on disable.
import { successToast } from "../../shared/Toast";
import { C } from "../reporting/reportingStyles";
import { RCard, Badge } from "../reporting/ReportingShared";
import {
  getFulfillmentConfigs,
  saveFulfillmentConfig,
  setFulfillmentPaused,
  disableFulfillmentMode,
  bulkSaveFulfillmentConfigs,
} from "../../utils/fulfillmentApi";
import { FULFILLMENT_MODES, MODE_LABELS, UNAVAILABLE_LABELS } from "../../constants/fulfillment";

/**
 * Curbside / drive-thru settings for one branch.
 *
 * Deliberately its own card with its own save, NOT folded into the branch form:
 *
 *  - each mode is a separate record, so one Save button for everything would make an edit
 *    to curbside silently rewrite drive-thru;
 *  - the pause switch has to be one tap during a rush, and must never carry a half-edited
 *    form with it — so it writes immediately through its own endpoint.
 *
 * There is no "enabled" checkbox because the server has no such column: the CONFIG ROW
 * EXISTING is what makes the branch offer the mode. Enable = create the row, disable =
 * delete it, pause = a temporary override on top.
 */
const NUMERIC_FIELDS = ["capacity", "prepMinutes", "feeAmount", "geofenceRadiusM", "geofenceLat", "geofenceLng"];

const blankDraft = () => ({
  capacity: "",
  prepMinutes: "",
  feeAmount: "",
  pickupPointEn: "",
  pickupPointAr: "",
  geofenceLat: "",
  geofenceLng: "",
  geofenceRadiusM: 150,
  // null = follow the branch's own weekly schedule (the default and the common case).
  // An array of 7 day-rows = this mode keeps its own hours.
  hoursOverride: null,
});

const draftFrom = (mode) => {
  if (!mode?.offered) return blankDraft();
  return {
    capacity: mode.capacity ?? "",
    prepMinutes: mode.prepMinutes ?? "",
    feeAmount: mode.feeAmount ?? "",
    pickupPointEn: mode.pickupPointEn ?? "",
    pickupPointAr: mode.pickupPointAr ?? "",
    geofenceLat: mode.geofenceLat ?? "",
    geofenceLng: mode.geofenceLng ?? "",
    geofenceRadiusM: mode.geofenceRadiusM ?? 150,
    hoursOverride: Array.isArray(mode.hoursOverride) ? mode.hoursOverride : null,
  };
};

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Normalise whatever the server sent into exactly 7 rows, so the editor never has to cope
// with a partial week.
const sevenDays = (rows) =>
  [0, 1, 2, 3, 4, 5, 6].map((weekday) => {
    const r = (rows || []).find((x) => Number(x?.weekday) === weekday) || {};
    return {
      weekday,
      isClosed: !!r.isClosed,
      is24Hours: !!r.is24Hours,
      openTime: r.openTime || "",
      closeTime: r.closeTime || "",
    };
  });

const BranchFulfillment = ({ branchId }) => {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingMode, setSavingMode] = useState(null);
  const [activeTab, setActiveTab] = useState(FULFILLMENT_MODES[0]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkOverwrite, setBulkOverwrite] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    const res = await getFulfillmentConfigs(branchId);
    setData(res);
    setDrafts(
      FULFILLMENT_MODES.reduce((acc, m) => ({ ...acc, [m]: draftFrom(res?.modes?.[m]) }), {})
    );
    setLoading(false);
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const setField = (mode, key, value) =>
    setDrafts((prev) => ({ ...prev, [mode]: { ...prev[mode], [key]: value } }));

  const onSave = async (mode) => {
    setSavingMode(mode);
    const draft = drafts[mode] || {};
    const payload = { branchId, mode };
    Object.entries(draft).forEach(([k, v]) => {
      // hoursOverride is an array or null — it must pass through untouched, not be run
      // through the ""-to-null / Number() coercion the scalar fields use.
      if (k === "hoursOverride") { payload[k] = v; return; }
      // "" clears the field server-side; a number field must not be sent as a string.
      payload[k] = v === "" ? null : NUMERIC_FIELDS.includes(k) ? Number(v) : v;
    });

    const res = await saveFulfillmentConfig(payload);
    setSavingMode(null);
    if (res) {
      successToast(res.created ? `${MODE_LABELS[mode]} enabled` : "Settings saved");
      load();
    }
  };

  const onTogglePause = async (mode, isPaused) => {
    const res = await setFulfillmentPaused({ branchId, mode, isPaused });
    if (res) {
      successToast(isPaused ? `${MODE_LABELS[mode]} paused` : `${MODE_LABELS[mode]} resumed`);
      load();
    }
  };

  // Copy the settings currently on screen out to every branch. Uses the same field set as
  // the single save, so what you see here is exactly what lands everywhere.
  const onApplyToAll = async () => {
    setBulkBusy(true);
    setBulkResult(null);
    const draftValues = drafts[activeTab] || {};
    const payload = { mode: activeTab, allBranches: true, overwrite: bulkOverwrite };
    Object.entries(draftValues).forEach(([k, v]) => {
      // Hours ARE policy, so unlike the pin they do travel across branches.
      if (k === "hoursOverride") { payload[k] = v; return; }
      payload[k] = v === "" ? null : NUMERIC_FIELDS.includes(k) ? Number(v) : v;
    });

    const res = await bulkSaveFulfillmentConfigs(payload);
    setBulkBusy(false);
    if (res) {
      setBulkResult(res);
      successToast(`${res.created} enabled, ${res.updated} updated, ${res.skipped} left as-is`);
      load();
    }
  };

  // Switching to custom hours starts from the branch's ACTUAL schedule rather than a blank
  // week — nobody wants to retype seven days to change one of them.
  const setUseCustomHours = (useCustom) =>
    setField(activeTab, "hoursOverride", useCustom ? sevenDays(data?.branchSchedule) : null);

  const setDayField = (weekday, key, value) => {
    // Read from `drafts` rather than the `draft` const below, so this does not depend on
    // declaration order inside the component body.
    const days = sevenDays(drafts[activeTab]?.hoursOverride);
    const day = { ...days[weekday], [key]: value };
    // Closed and 24-hour are mutually exclusive, and both make the time inputs meaningless
    // — mirrors how the branch's own schedule editor behaves.
    if (key === "isClosed" && value) Object.assign(day, { is24Hours: false, openTime: "", closeTime: "" });
    if (key === "is24Hours" && value) Object.assign(day, { isClosed: false, openTime: "", closeTime: "" });
    days[weekday] = day;
    setField(activeTab, "hoursOverride", days);
  };

  const onDisable = async (mode) => {
    // The server refuses while customers are still waiting, and its message names the count
    // — surfaced by the api module's errorToast rather than swallowed here.
    const res = await disableFulfillmentMode({ branchId, mode });
    if (res) {
      successToast(`${MODE_LABELS[mode]} disabled`);
      load();
    }
  };

  if (!branchId) return null;

  const modes = data?.modes || {};
  const current = modes[activeTab];
  const draft = drafts[activeTab] || blankDraft();

  // The geofence silently falls back to the branch's own coordinates, and most rows in this
  // database are "0"/"0" — which reads as the Gulf of Guinea. Say so plainly here rather
  // than letting someone wonder why verification never fires.
  const branchLat = Number(data?.branchCoordinates?.lat);
  const branchLng = Number(data?.branchCoordinates?.lng);
  const branchCoordsUnusable =
    !Number.isFinite(branchLat) || !Number.isFinite(branchLng) || (branchLat === 0 && branchLng === 0);
  const modePinSet = draft.geofenceLat !== "" && draft.geofenceLng !== "";

  const usingCustomHours = Array.isArray(draft.hoursOverride);
  const branchDays = sevenDays(data?.branchSchedule);
  const branchHoursSummary = branchDays.every((d) => d.isClosed)
    // Worth calling out: a branch with every day closed makes the mode permanently
    // unorderable, and the cause is the branch schedule, not anything on this screen.
    ? "The branch schedule has every day closed, so this mode will never be orderable."
    : branchDays.every((d) => d.is24Hours)
      ? "The branch is open 24 hours, so this mode is too."
      : "Opening and closing with the branch each day.";

  return (
    <RCard title={<><Truck size={16} color={C.coral} /> Curbside &amp; Drive-Thru</>}>
      {loading && !data ? (
        <div style={{ fontSize: 13, color: C.textMed }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 18, borderBottom: `1px solid ${C.borderLight}` }}>
            {FULFILLMENT_MODES.map((mode) => {
              const m = modes[mode];
              const isActive = activeTab === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setActiveTab(mode)}
                  style={{
                    background: "none",
                    border: "none",
                    borderBottom: `2px solid ${isActive ? C.coral : "transparent"}`,
                    color: isActive ? C.coral : C.textMed,
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 13,
                    padding: "8px 14px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {MODE_LABELS[mode]}
                  {m?.offered ? (
                    m.isPaused
                      ? <Badge color={C.orange} bg={C.orangeLight}>Paused</Badge>
                      : m.live?.orderableNow
                        ? <Badge color={C.green} bg={C.greenLight}>Live</Badge>
                        : <Badge color={C.textMed} bg={C.borderLight}>Closed</Badge>
                  ) : (
                    <Badge color={C.textLight} bg={C.borderLight}>Off</Badge>
                  )}
                </button>
              );
            })}
          </div>

          {!current?.offered ? (
            <div style={{ padding: "8px 0 16px" }}>
              <div style={{ fontSize: 13, color: C.textMed, marginBottom: 14 }}>
                This branch does not offer {MODE_LABELS[activeTab]}. Fill in the settings below
                and save to turn it on for customers.
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                marginBottom: 18,
                borderRadius: 10,
                background: current.isPaused ? C.orangeLight : C.borderLight,
                border: `1px solid ${current.isPaused ? C.orange : C.border}`,
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  {current.isPaused ? "Paused — customers cannot order this way" : "Accepting orders"}
                </div>
                <div style={{ fontSize: 11, color: C.textMed, marginTop: 2 }}>
                  {current.live?.orderableNow
                    ? `${current.activeNow ?? 0} active now${current.capacity ? ` of ${current.capacity}` : ""}`
                    : UNAVAILABLE_LABELS[current.live?.reason] || "Not orderable right now"}
                </div>
              </div>
              {/* Applies immediately — no Save needed. This is the rush switch. */}
              <ToggleSwitch
                checked={!current.isPaused}
                handleToggle={(isChecked) => onTogglePause(activeTab, !isChecked)}
              />
            </div>
          )}

          {branchCoordsUnusable && !modePinSet && (
            <div
              style={{
                display: "flex",
                gap: 10,
                padding: "10px 12px",
                marginBottom: 16,
                borderRadius: 8,
                background: C.orangeLight,
                border: `1px solid ${C.orange}`,
              }}
            >
              <AlertTriangle size={16} color={C.orange} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 12, color: C.text }}>
                This branch has no usable coordinates, so arrivals cannot be location-verified —
                every customer will show as “Manual” to staff. Set a pin below, or fix the
                branch’s latitude and longitude above.
              </div>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 14,
              marginBottom: 16,
            }}
          >
            <Field label="Prep time (minutes)" hint="Shown to the customer before they pay">
              <input type="number" min="0" style={inputStyle} value={draft.prepMinutes}
                onChange={(e) => setField(activeTab, "prepMinutes", e.target.value)} />
            </Field>

            <Field label="Capacity" hint="Max cars at once. Leave blank for unlimited">
              <input type="number" min="0" style={inputStyle} value={draft.capacity}
                onChange={(e) => setField(activeTab, "capacity", e.target.value)} />
            </Field>

            <Field label="Fee (VAT included)" hint="What the customer pays for this mode. Blank or 0 = no fee">
              <input type="number" min="0" step="0.01" style={inputStyle} value={draft.feeAmount}
                onChange={(e) => setField(activeTab, "feeAmount", e.target.value)} />
            </Field>

            <Field label="Arrival radius (metres)" hint="A lane approach needs more than a parking bay">
              <input type="number" min="0" style={inputStyle} value={draft.geofenceRadiusM}
                onChange={(e) => setField(activeTab, "geofenceRadiusM", e.target.value)} />
            </Field>

            <Field label="Pin latitude" hint="Blank falls back to the branch coordinates">
              <input type="text" style={inputStyle} value={draft.geofenceLat}
                onChange={(e) => setField(activeTab, "geofenceLat", e.target.value)} />
            </Field>

            <Field label="Pin longitude" hint="Set both, or neither">
              <input type="text" style={inputStyle} value={draft.geofenceLng}
                onChange={(e) => setField(activeTab, "geofenceLng", e.target.value)} />
            </Field>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 14,
              marginBottom: 16,
            }}
          >
            <Field label="Where to go (English)" hint="e.g. “Bay area by the side entrance”">
              <input type="text" style={inputStyle} value={draft.pickupPointEn}
                onChange={(e) => setField(activeTab, "pickupPointEn", e.target.value)} />
            </Field>
            <Field label="Where to go (Arabic)" hint="Shown to Arabic-language customers">
              <input type="text" dir="rtl" style={inputStyle} value={draft.pickupPointAr}
                onChange={(e) => setField(activeTab, "pickupPointAr", e.target.value)} />
            </Field>
          </div>

          {/* Hours. Default is to follow the branch, which is what nearly every branch wants
              — a drive-thru that opens earlier or closes later is the exception. */}
          <div
            style={{
              padding: "12px 14px",
              marginBottom: 16,
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: C.white,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  {usingCustomHours ? `Custom ${MODE_LABELS[activeTab]} hours` : "Follows the branch's hours"}
                </div>
                <div style={{ fontSize: 11, color: C.textMed, marginTop: 2 }}>
                  {usingCustomHours
                    ? "This mode ignores the branch schedule and uses the days below."
                    : branchHoursSummary}
                </div>
              </div>
              <ToggleSwitch
                checked={usingCustomHours}
                handleToggle={(isChecked) => setUseCustomHours(isChecked)}
              />
            </div>

            {usingCustomHours && (
              <div style={{ overflowX: "auto", marginTop: 12, borderRadius: 8, border: `1px solid ${C.borderLight}` }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: C.bg }}>
                      <th style={hoursTh}>Day</th>
                      <th style={{ ...hoursTh, textAlign: "center" }}>Status</th>
                      <th style={hoursTh}>Opens</th>
                      <th style={hoursTh}>Closes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sevenDays(draft.hoursOverride).map((d) => {
                      const dimmed = d.isClosed || d.is24Hours;
                      return (
                        <tr key={d.weekday} style={{ borderTop: `1px solid ${C.borderLight}` }}>
                          <td style={{ ...hoursTd, fontWeight: 700 }}>{WEEKDAYS[d.weekday]}</td>
                          <td style={hoursTd}>
                            <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
                              <label style={hoursCheckLabel}>
                                <input type="checkbox" checked={d.isClosed}
                                  onChange={(e) => setDayField(d.weekday, "isClosed", e.target.checked)}
                                  style={{ accentColor: C.coral }} />
                                Closed
                              </label>
                              <label style={hoursCheckLabel}>
                                <input type="checkbox" checked={d.is24Hours}
                                  onChange={(e) => setDayField(d.weekday, "is24Hours", e.target.checked)}
                                  style={{ accentColor: C.blue }} />
                                24 Hours
                              </label>
                            </div>
                          </td>
                          <td style={hoursTd}>
                            <input type="time" value={d.openTime} disabled={dimmed}
                              onChange={(e) => setDayField(d.weekday, "openTime", e.target.value)}
                              style={{ ...inputStyle, padding: "6px 8px", fontSize: 12, opacity: dimmed ? 0.45 : 1 }} />
                          </td>
                          <td style={hoursTd}>
                            <input type="time" value={d.closeTime} disabled={dimmed}
                              onChange={(e) => setDayField(d.weekday, "closeTime", e.target.value)}
                              style={{ ...inputStyle, padding: "6px 8px", fontSize: 12, opacity: dimmed ? 0.45 : 1 }} />
                            {/* Closing at or before opening means the window crosses midnight —
                                the server handles that, but say so or it reads like a typo. */}
                            {!dimmed && d.openTime && d.closeTime && d.closeTime <= d.openTime && (
                              <div style={{ color: C.blue, fontSize: 10, marginTop: 3, fontWeight: 600 }}>
                                Next day
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {bulkOpen && (
            <div
              style={{
                padding: "14px 16px",
                marginBottom: 16,
                borderRadius: 10,
                background: C.coralLight,
                border: `1px solid ${C.coral}`,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                Apply these {MODE_LABELS[activeTab]} settings to every branch
              </div>
              <div style={{ fontSize: 12, color: C.textMed, marginBottom: 10 }}>
                Turns {MODE_LABELS[activeTab]} on across the whole brand using the prep time,
                capacity, <strong>fee</strong>, radius and pickup-point text above. Hours always
                follow each branch&rsquo;s own schedule. The <strong>pin coordinates are not
                copied</strong> — each branch keeps its own location, so drop pins per branch
                afterwards.
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={bulkOverwrite}
                  onChange={(e) => setBulkOverwrite(e.target.checked)}
                  style={{ accentColor: C.coral }}
                />
                {/* Off by default: the usual case is rolling out to the rest of the estate
                    after a few branches were tuned by hand, and flattening those silently
                    would be very hard to spot. */}
                Also overwrite branches that already offer it
              </label>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button type="button" onClick={onApplyToAll} disabled={bulkBusy} style={primaryBtnStyle}>
                  {bulkBusy ? "Applying…" : "Apply to all branches"}
                </button>
                <button type="button" onClick={() => setBulkOpen(false)} style={ghostBtnStyle}>
                  Cancel
                </button>
              </div>

              {bulkResult && (
                <div style={{ fontSize: 12, color: C.text, marginTop: 12 }}>
                  <strong>{bulkResult.created}</strong> enabled ·{" "}
                  <strong>{bulkResult.updated}</strong> updated ·{" "}
                  <strong>{bulkResult.skipped}</strong> left as-is
                  {bulkResult.skippedBranches?.length ? (
                    <div style={{ color: C.textMed, marginTop: 4 }}>
                      Kept their own settings: {bulkResult.skippedBranches.join(", ")}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: `1px solid ${C.borderLight}`,
              paddingTop: 16,
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {current?.offered ? (
                <button type="button" onClick={() => onDisable(activeTab)} style={dangerBtnStyle}>
                  <Power size={14} /> Stop offering {MODE_LABELS[activeTab]}
                </button>
              ) : null}
              <button type="button" onClick={() => setBulkOpen((v) => !v)} style={ghostBtnStyle}>
                <Copy size={14} /> Apply to all branches
              </button>
            </div>

            <button
              type="button"
              onClick={() => onSave(activeTab)}
              disabled={savingMode === activeTab}
              style={primaryBtnStyle}
            >
              {savingMode === activeTab
                ? "Saving…"
                : current?.offered ? "Save settings" : `Enable ${MODE_LABELS[activeTab]}`}
            </button>
          </div>
        </>
      )}
    </RCard>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
      {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>{hint}</div>}
  </div>
);

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  fontSize: 13,
  color: C.text,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  outline: "none",
  background: C.white,
};

const primaryBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 600,
  color: C.white,
  background: C.coral,
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
};

const hoursTh = {
  textAlign: "left",
  padding: "8px 10px",
  fontSize: 10,
  fontWeight: 700,
  color: C.textMed,
  textTransform: "uppercase",
  letterSpacing: 0.6,
};

const hoursTd = { padding: "6px 10px", color: C.text };

const hoursCheckLabel = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  cursor: "pointer",
  fontSize: 11,
  color: C.text,
  margin: 0,
};

const ghostBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "9px 14px",
  fontSize: 12,
  fontWeight: 600,
  color: C.textMed,
  background: C.white,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  cursor: "pointer",
};

const dangerBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "9px 14px",
  fontSize: 12,
  fontWeight: 600,
  color: "#B91C1C",
  background: "#FEF2F2",
  border: "1px solid #FECACA",
  borderRadius: 8,
  cursor: "pointer",
};

export default BranchFulfillment;
